/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { Beruf } from '@/domain/mitarbeiter/features/rotationsplan/entities/Beruf';
import { Markierung } from '@/domain/mitarbeiter/features/rotationsplan/entities/Markierung';
import type { RotationsplanVorlageDetails } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanVorlageById/useRotationsplanVorlageById';
import { useUpdateRotationsplanVorlage } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useUpdateRotationsplanVorlage/useUpdateRotationsplanVorlage';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { useForm as useTanStackForm } from '@tanstack/react-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import z from 'zod';

export function useForm({
  vorlage,
  berufe,
}: {
  vorlage: RotationsplanVorlageDetails;
  berufe: Beruf[];
}) {
  const [updateRotationsplanVorlage] = useUpdateRotationsplanVorlage();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Fehler vom Server, gilt nur bis zur nächsten Änderung im Formular
  const [titelVergeben, setTitelVergeben] = useState(false);

  const schema = z.object({
    beruf: z.instanceof(Beruf, {
      message: t('generalValidationError.required'),
    }),
    titel: z
      .string()
      .trim()
      .min(1, {
        message: t(
          'domain.mitarbeiter.rotationsplan.vorlagen.createVorlage.fields.titel.validationError',
        ),
      }),
  });

  type FormValues = z.infer<typeof schema>;

  async function handleSubmit(value: FormValues) {
    const showError = () =>
      toast({
        variant: 'destructive',
        title: t(
          'domain.mitarbeiter.rotationsplan.vorlagen.updateVorlage.error',
        ),
      });

    try {
      const result = await updateRotationsplanVorlage({
        input: {
          vorlageId: vorlage.id,
          berufId: value.beruf.id,
          titel: value.titel.trim(),
        },
      });
      const response = result.data?.mitarbeiter_updateRotationsplanVorlage;

      if (
        response?.error?.__typename ===
        'mitarbeiter_rotationsplanVorlageTitelVergeben'
      ) {
        setTitelVergeben(true);
        return;
      }

      if (!response?.success) {
        showError();
        return;
      }

      toast({
        title: t(
          'domain.mitarbeiter.rotationsplan.vorlagen.updateVorlage.success',
        ),
      });
      navigate(`/mitarbeiter/rotationsplaene/vorlagen/${vorlage.id}`, {
        replace: true,
      });
    } catch (error) {
      Sentry.captureException(error);
      showError();
    }
  }

  const form = useTanStackForm({
    defaultValues: {
      beruf:
        berufe.find(({ id }) => id === vorlage.beruf.id) ??
        new Beruf({
          ...vorlage.beruf,
          markierung: vorlage.beruf.markierung as unknown as Markierung,
          abkuerzung: vorlage.beruf.abkuerzung ?? '',
        }),
      titel: vorlage.titel,
    },
    validators: {
      onChange: schema,
    },
    listeners: {
      onChange: () => setTitelVergeben(false),
    },
    onSubmit: async ({ value }) => await handleSubmit(value),
  });

  return { form, titelVergeben };
}
